/* eslint-disable  @typescript-eslint/no-non-null-assertion */
import { useThemeProps } from '@mui/material';
import {
  default as MuiChip,
  ChipTypeMap as MuiChipTypeMap,
  ChipProps as MuiChipProps,
} from '@mui/material/Chip';
import { OverrideProps } from '@mui/material/OverridableComponent';
import * as React from 'react';
import { useIsAncestorDisabled } from 'src/context/DisabledAncestorContext/DisabledAncestorContext';
import {
  ExtendAndOmitTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';
import { inProgressColor } from './style/colors';
import * as UnifyIcons from 'src/components/UnifyIcons';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ChipOwnProps {}

/**
 * Deprecate props that aren't supported in Unify.
 */
type UnsupportedProps = {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  deleteIcon?: MuiChipProps['deleteIcon'];

  /**
   * @deprecated Unsupported prop: This prop is no longer supported in Unify design guidelines and will be removed in the next major.
   *
   * Setting the prop will no longer change the appearance of a Chip.
   *
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  variant?: MuiChipProps['variant'];
};

export type ChipTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiChipTypeMap['defaultComponent']
> = ExtendAndOmitTypeMap<
  MuiChipTypeMap,
  AdditionalProps & ChipOwnProps & UnsupportedProps,
  keyof UnsupportedProps,
  RootComponent
>;

export type ChipProps<
  RootComponent extends React.ElementType = MuiChipTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<ChipTypeMap<AdditionalProps, RootComponent>, RootComponent>;

export const Chip = forwardRefToOverridableComponent<ChipTypeMap, ChipProps>(
  function Chip(inProps, forwardedRef) {
    const props = useThemeProps({ props: inProps, name: 'MuiChip' });
    const {
      color: colorProp,
      // Prevent props.children from being passed along to MUI Chip since it's not supported.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      children,
      disabled: disabledProp,
      avatar,
      ...rest
    } = props;

    if (process.env.NODE_ENV !== 'production') {
      if (colorProp && avatar) {
        console.warn(
          'Setting color on a Chip with an Avatar does nothing. The color will be overridden to match the Avatar color.'
        );
      }
      if (colorProp === 'secondary') {
        console.warn(
          'There is no secondary Chip color in Unify so setting the color to secondary results in default Chip colors. Please reach out to us in #unify-design-system-support with your designer if you have a use case for a new Chip color.'
        );
      }
    }

    let color: typeof colorProp = colorProp ?? 'default';
    if (colorProp === 'inProgress') {
      color = inProgressColor; // See inProgressColor for more info
    }

    const ancestorDisabled = useIsAncestorDisabled();
    const disabled = disabledProp ?? ancestorDisabled ?? false;
    return (
      <MuiChip
        disabled={disabled}
        color={color}
        ref={forwardedRef}
        deleteIcon={<UnifyIcons.CancelFilled data-testid={'CancelIcon'} />}
        avatar={avatar}
        {...rest}
      />
    );
  }
);

export default Chip;
